import React, { useMemo, useContext, useCallback, useEffect } from 'react';
import { withTranslation, Trans } from 'react-i18next';
import { Emoji } from 'emoji-mart';

import config from 'config/index';

import { useGetCurrentPlanProps } from 'shared-hooks/team';
import { convertBytesToHumanReadable } from 'utils/file';
import { trackEvent } from 'services/events-tracker-service';

import { AlertContext } from 'context/AlertContext';

import PlanUpgradeButton from 'components/PlanUpgradeButton';
import Alert from 'components/alert';

const QuotaAlerts = ({ alert, t }) => {
    const alertCtx = useContext(AlertContext);
    const planProps = useGetCurrentPlanProps();

    const handleUpgradeClick = useCallback(() => {
        alertCtx.hideQuotaAlert();
    }, [alertCtx]);

    const message = useMemo(() => {
        if (!alert) {
            return null;
        }

        switch (alert.type) {
            case 'projects':
                if (config.BILLING_ENABLED) {
                    return <Trans i18nKey="billing:quotaAlerts.projects"
                        components={{
                            PlanUpgradeButton: <PlanUpgradeButton reason="projects" onClick={handleUpgradeClick} />
                        }}
                        values={{
                            plan: planProps.name,
                            count: planProps.quota.projects
                        }}
                    />
                }

                return <Trans i18nKey="billing:quotaAlerts.projects_noBilling"
                    values={{
                        plan: planProps.name,
                        count: planProps.quota.projects
                    }}
                />;

            case 'clients':
                if (config.BILLING_ENABLED) {
                    return <Trans i18nKey="billing:quotaAlerts.clients"
                        components={{
                            PlanUpgradeButton: <PlanUpgradeButton reason="clients" onClick={handleUpgradeClick} />
                        }}
                    />
                }

                return <Trans i18nKey="billing:quotaAlerts.clients_noBilling" />;

            case 'timeTracking':
                if (config.BILLING_ENABLED) {
                    return <Trans i18nKey="billing:quotaAlerts.timeTracking"
                        components={{
                            PlanUpgradeButton: <PlanUpgradeButton reason="timeTracking" onClick={handleUpgradeClick} />
                        }}
                    />
                }

                return <Trans i18nKey="billing:quotaAlerts.timeTracking_noBilling" />;

            case 'fileSize':
                if (config.BILLING_ENABLED) {
                    return <Trans i18nKey="billing:quotaAlerts.fileSize"
                        components={{
                            PlanUpgradeButton: <PlanUpgradeButton reason="fileSize" onClick={handleUpgradeClick} />
                        }}
                        values={{
                            plan: planProps.name,
                            size: convertBytesToHumanReadable(planProps.quota.fileBytes)
                        }}
                    />
                }

                return <Trans i18nKey="billing:quotaAlerts.fileSize_noBilling"
                    values={{
                        plan: planProps.name,
                        size: convertBytesToHumanReadable(planProps.quota.fileBytes)
                    }}
                />;

            case 'storage':
                if (config.BILLING_ENABLED) {
                    return <Trans i18nKey="billing:quotaAlerts.storage"
                        components={{
                            PlanUpgradeButton: <PlanUpgradeButton reason="storage" onClick={handleUpgradeClick} />
                        }}
                        values={{
                            plan: planProps.name,
                            size: convertBytesToHumanReadable(planProps.quota.storageBytes)
                        }}
                    />
                }

                return <Trans i18nKey="billing:quotaAlerts.storage_noBilling"
                    values={{
                        plan: planProps.name,
                        size: convertBytesToHumanReadable(planProps.quota.storageBytes)
                    }}
                />;


        }
    }, [alert, planProps, handleUpgradeClick, t]);

    useEffect(() => {
        if (alert?.type) {
            trackEvent('PLAN_QUOTA_REACHED.TRIGGER', {
                type: alert.type,
                billingEnabled: config.BILLING_ENABLED
            });
        }
    }, [alert]);

    if (!alert) {
        return null;
    }

    return (
        <Alert type="default" icon={<Emoji emoji="tada" size={24} />}>
            { message }
        </Alert>
    )
}

export default withTranslation('billing')(QuotaAlerts);
